import React, { useState, useEffect } from "react";
import axios from "axios";
import api from '../../../Axios'

const ManageGallery = () => {
  const [media, setMedia] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState({ images: [], videos: [] });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Fetch media data from the server
  const fetchMedia = async () => {
    try {
      const { data } = await api.get(`${process.env.REACT_APP_BASE_URL}/api/v1/gallery/media`);
      setMedia(data);
    } catch (error) {
      console.error(error);
      setMessage("Failed to fetch media!");
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setSelectedFiles((prev) => ({ ...prev, [name]: files }));
  };

  // Upload selected files
  const handleUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const formData = new FormData();
    for (let image of selectedFiles.images) {
      formData.append("images", image);
    }
    for (let video of selectedFiles.videos) {
      formData.append("videos", video);
    }

    try {
      const { data } = await api.post(`${process.env.REACT_APP_BASE_URL}/api/v1/gallery/create`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessage(data.message);
      fetchMedia();
      setSelectedFiles({ images: [], videos: [] });
    } catch (error) {
      console.error(error);
      setMessage("Failed to upload media!");
    } finally {
      setLoading(false);
    }
  };

  // Delete a media entry
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this media?")) return;
    setLoading(true);

    try {
      const { data } = await api.delete(`/api/v1/gallery/media/${id}`);
      setMessage(data.message);
      fetchMedia();
    } catch (error) {
      console.error(error);
      setMessage("Failed to delete media!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 bg-teal-50 rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-teal-600 mb-6 text-center">
        Manage Gallery
      </h1>

      {message && (
        <p className="mb-4 text-center text-teal-600 font-medium">{message}</p>
      )}

      {/* Upload Form */}
      <form onSubmit={handleUpload} className="mb-6">
        <div className="mb-4">
          <label className="block font-medium text-teal-600 mb-2">
            Select Images
          </label>
          <input
            type="file"
            name="images"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="block w-full px-4 py-2 border border-teal-300 rounded-md"
          />
        </div>
        <div className="mb-4">
          <label className="block font-medium text-teal-600 mb-2">
            Select Videos
          </label>
          <input
            type="file"
            name="videos"
            multiple
            accept="video/*"
            onChange={handleFileChange}
            className="block w-full px-4 py-2 border border-teal-300 rounded-md"
          />
        </div>
        <button
          type="submit"
          className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition-colors duration-300"
          disabled={loading}
        >
          {loading ? "Uploading..." : "Upload"}
        </button>
      </form>

      {/* Media Display */}
      <div>
        <h2 className="text-2xl font-bold text-teal-600 mb-6">Gallery</h2>
        {media.length === 0 ? (
          <p className="text-center text-teal-600">No media available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {media.map((item) => (
              <div
                key={item._id}
                className="border border-teal-300 p-4 rounded-lg shadow-md bg-white"
              >
                {/* <h3 className="font-semibold text-teal-600 mb-2">Media ID: {item._id}</h3> */}

                {/* Display Images */}
                {item.images.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-teal-500 mb-2">Images:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {item.images.map((image) => (
                        <img
                          key={image}
                          src={`${process.env.REACT_APP_BASE_URL}/api/uploads/images/${image}`}
                          alt="Gallery"
                          className="w-full h-32 object-cover rounded-md"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Display Videos */}
                {item.videos.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-teal-500 mb-2">Videos:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {item.videos.map((video) => (
                        <video
                          key={video}
                          controls
                          className="w-full h-32 object-cover rounded-md"
                        >
                          <source src={`${process.env.REACT_APP_BASE_URL}/api/uploads/videos/${video}`} />
                          Your browser does not support the video tag.
                        </video>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delete Button */}
                <button
                  onClick={() => handleDelete(item._id)}
                  className="bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors duration-300"
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageGallery;
